import * as React from "react";
import { styled } from "@mui/material/styles";
import DrawAreaView from "./DrawAreaView";
import Editor from "../editor/Editor";
import { inject } from "mobx-react";
import { observer } from "mobx-react";
import {
  createNewNodeToolChain,
  createTransformToolChain,
  createConnectNodeToolChain,
  createPanToolChain,
} from "../editor/toolChains";
import DeleteLayerTool from "../editor/DeleteLayerTool";
import NodeLayer from "../editor/NodeLayer";
import NodeLayerPanelView from "./NodeLayerPanelView";
import { RightPanel } from "./EditorViewStyles";
import autobind from "autobind-decorator";
import ErrorModal from "./ErrorModal";
import ConnectionLayer from "../editor/ConnectionLayer";
import ConnectionLayerPanelView from "./ConnectionLayerPanelView";
import QuestionnaireModal from "./QuestionnaireModal";
import { Box, Fade, Tooltip, Typography } from "@mui/material";
import Camera from "../editor/Camera";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import Vector2 from "../editor/Vector2";
import { notification } from "antd";
import "antd/dist/antd.css";
import duplicateBox from "../assets/icons/duplicate-box.svg";
import duplicateSection from "../assets/icons/duplicate-section.svg";
import crownIcon from "../assets/icons/crown.svg";
import { authorizeFeatureAccess, isAuthorizedUser } from "../util/util";
import AccountService from "../service/AccountService";
import { observable } from "mobx";
import UpgradeDialog from "../pages/common/UpgradeDialog";
import { RouterStore } from "mobx-react-router";
import DuplicateNodeTool from "../editor/DuplicateNodeTool";
import DuplicateSectionTool from "../editor/DuplicateSectionTool";

const Container = styled("div")({
  position: "relative",
  display: "flex",
  flexDirection: "row",
  flexGrow: 1,
  overflow: "hidden",
});

const Toolbox = styled("div")({
  position: "absolute",
  left: 0,
  top: 0,
  width: "84px",
  height: "100%",
  overflowY: "hidden",
  paddingLeft: "24px",
  paddingTop: "24px",
  pointerEvents: "none",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
});
const HorizontalToolBox = styled("div")({
  position: "absolute",
  left: 0,
  right: 0,

  top: "10px",
  width: "fit-content",
  margin: "auto",
  padding: "12px 24px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "10px",
  backgroundColor: "#fff",
  borderRadius: "8px",
  zIndex: "1000",
  border: "2px solid #d5d6d7",
  boxShadow:
    "-4px -4px 8px 0px rgba(0, 32, 38, 0.04), 4px 4px 8px 0px rgba(0, 32, 38, 0.04)",
});

const ToolOption = styled("div")({
  border: "2px solid #d7dadb",
  display: "flex",
  alignItems: "center",
  padding: "10px 16px",
  borderRadius: "8px",
  fontSize: "16px",
  fontWeight: "500",
  cursor: "pointer",

  "&:hover": {
    backgroundColor: "#fbfbfb",
  },
});

const ToolIcon = styled("img")({
  color: "#464646",
  width: "20px",
  height: "20px",
  marginRight: "4px",
});

const ArrowToggle = styled("div")({
  position: "absolute",
  left: "-40",
  top: "10",
  height: "30px",
  width: "40px",
  display: "none",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#3F51B5",
  borderRadius: "6px 0 0 6px",
  color: "#fff",
  cursor: "pointer",
  "@media screen and (max-width: 1025px)": {
    display: "flex !important",
  },
});

const Tool = styled(Tooltip)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  boxShadow: "0 1px 5px rgba(0, 0, 0, 0.15)",
  border: "1px solid rgba(0, 0, 0, 0.1)",
  width: "48px",
  height: "48px",
  cursor: "pointer",
  transition: "all 150ms ease-in-out",
  color: "rgb(70, 70, 70)",
  background: "white",
  pointerEvents: "all",

  margintTop: "24px",
  marginBottom: "24px",
  userSelect: "none",

  "&:hover, &.selected": {
    transform: "scale(1.2)",
    border: "1px solid rgba(137, 70, 255, 0.5)",
  },
});

const EmptyRightPanel = styled("div")({
  display: "flex",
  backgroundColor: "#f0f0f0",
  color: "#787878",
  padding: "18px 16px",
});

type EditorViewProps = {
  editor?: Editor;
  account?: AccountService;
  routing?: RouterStore;
  className?: string;
  style?: React.CSSProperties;
};

type EditorViewState = {
  isOpen: boolean;
  resizeWidth: any;
};

@inject("editor")
@inject("account")
@inject("routing")
@observer
export default class EditorView extends React.Component<
  EditorViewProps,
  EditorViewState
> {
  modalContainer = null;

  @observable
  upgradeDialogOpen: boolean = false;

  @autobind
  gotoPlan() {
    var { routing } = this.props;
    routing.push(`/plan`);
  }

  state: EditorViewState = {
    isOpen: true,
    resizeWidth: 0,
  };

  @autobind
  removeError() {
    this.props.editor.setError(null);
  }

  handleZoom(factor: number) {
    const zoomScaleFactor = -0.05;

    var { editor } = this.props;
    var camera = editor.camera;
    var finalScale = camera.scale - camera.scale * factor * zoomScaleFactor;
    finalScale = Math.max(finalScale, 0.1);

    editor.setCamera(new Camera(camera.position, finalScale));
  }
  @autobind
  handleDelete() {
    var { editor } = this.props;
    const deleteLayer = new DeleteLayerTool(editor);
    deleteLayer.handleDelete();
  }

  @autobind
  handleBoxDuplication() {
    var { editor } = this.props;
    if (!editor.selectedLayer && !(editor.selectedLayer instanceof NodeLayer)) {
      notification.warning({
        message: "you need to select a box first",
        duration: 4,
        placement: "bottomRight",
      });
    }
    const duplicateLayer = new DuplicateNodeTool(editor);
    duplicateLayer.duplicate();
  }
  @autobind
  handleSectionDuplication() {
    var { editor } = this.props;
    if (!editor.selectedLayer && !(editor.selectedLayer instanceof NodeLayer)) {
      notification.warning({
        message: "you need to select a box first",
        duration: 4,
        placement: "bottomRight",
      });
    }
    const duplicateLayer = new DuplicateSectionTool(editor);
    duplicateLayer.duplicateSection();
  }

  @autobind
  handleBackTostartNode() {
    var { editor } = this.props;
    let position: Vector2 = editor.camera.position;
    const scale = 0.5;
    if (editor.startLayer) {
      const startLayerX = editor.startLayer?.getCenter().x;
      const startLayerY = editor.startLayer?.getCenter().y;
      position = new Vector2(
        startLayerX - editor.canvaSize.width,
        startLayerY - editor.canvaSize.height / 2
      );
    } else if (editor.rootLayerGroup?.layers.length > 0) {
      const nodeLayerX = editor.rootLayerGroup?.layers[0].getCenter().x;
      const nodeLayerY = editor.rootLayerGroup?.layers[0].getCenter().y;
      position = new Vector2(
        nodeLayerX - editor.canvaSize.width,
        nodeLayerY - editor.canvaSize.height / 2
      );
    } else {
      notification.warning({
        message: "you need to create a workflow first",
        duration: 4,
        placement: "bottomRight",
      });
    }
    editor.setCamera(new Camera(position, scale));
  }

  componentDidUpdate() {
    if (window.innerWidth >= 1025 && this.state.isOpen === false) {
      this.setState({
        isOpen: true,
      });
    }

    if (this.state.resizeWidth !== window.innerWidth) {
      window.addEventListener("resize", () => {
        this.setState({
          resizeWidth: window.innerWidth,
        });
      });
      window.removeEventListener("resize", () => {
        this.setState({
          resizeWidth: window.innerWidth,
        });
      });
    }
  }

  render() {
    var { editor, account } = this.props;
    if (this.modalContainer == null) {
      this.modalContainer = document.getElementById("app-content");
    }

    return (
      <Container className={this.props.className} style={this.props.style}>
        <div style={{ flexGrow: 1, position: "relative" }}>
          <DrawAreaView />
        </div>
        <Toolbox>
          <Tool
            title="Start Question"
            placement="right"
            className={
              editor.currentTool.name == "restart" ? "selected" : "null"
            }
            onClick={this.handleBackTostartNode}
          >
            <i className="material-icons">assignment_turned_in </i>
            {/* <AssignmentTurnedInIcon className="material-icons" /> */}
          </Tool>
          <Tool
            title="Select Item"
            placement="right"
            className={
              editor.currentTool.name == "transform" ? "selected" : "null"
            }
            onClick={() =>
              editor.setCurrentTool(createTransformToolChain(editor))
            }
          >
            <i className="material-icons"> touch_app </i>
          </Tool>

          <Tool
            title="Create new question"
            placement="right"
            className={
              editor.currentTool.name == "new_node" ? "selected" : "null"
            }
            onClick={() =>
              editor.setCurrentTool(createNewNodeToolChain(editor))
            }
          >
            <i className="material-icons"> crop_din </i>
          </Tool>

          <Tool
            title="Create Connector Arrow"
            placement="right"
            className={
              editor.currentTool.name == "connect_node" ? "selected" : "null"
            }
            onClick={() =>
              editor.setCurrentTool(createConnectNodeToolChain(editor))
            }
          >
            <i
              className="material-icons"
              style={{ transform: "rotateZ(-45deg) scale(1.1)" }}
            >
              {" "}
              arrow_upward{" "}
            </i>
          </Tool>

          <Tool
            title="Zoom In"
            placement="right"
            onClick={() => this.handleZoom(1)}
          >
            <i className="material-icons"> zoom_in </i>
          </Tool>

          <Tool
            title="Zoom Out"
            placement="right"
            onClick={() => this.handleZoom(-1)}
          >
            <i className="material-icons"> zoom_out </i>
          </Tool>

          <Tool title="Delete" placement="right" onClick={this.handleDelete}>
            <i className="material-icons"> delete </i>
          </Tool>
        </Toolbox>
        <Fade
          in={
            editor?.selectedLayer && editor.selectedLayer instanceof NodeLayer
          }
        >
          <HorizontalToolBox>
            <Typography variant="h6" mr="1rem">
              Select Action:
            </Typography>
            <ToolOption onClick={() => this.handleBoxDuplication()}>
              <ToolIcon
                src={duplicateBox}
                alt=""
                style={{ color: "#464646" }}
              />
              Duplicate Box
            </ToolOption>
            <ToolOption
              onClick={() =>
                authorizeFeatureAccess(
                  account?.userData,
                  () => (this.upgradeDialogOpen = true),
                  () => this.handleSectionDuplication()
                  // notification.info({
                  //   message:
                  //     "Stay tuned! The feature you have asked for is on its way.",
                  //   duration: 6,
                  //   placement: "bottomRight",
                  // })
                )
              }
            >
              <ToolIcon
                src={duplicateSection}
                alt=""
                style={{ color: "#464646" }}
              />
              Duplicate Section
              {!isAuthorizedUser(account?.userData) && (
                <img
                  src={crownIcon}
                  alt=""
                  style={{
                    right: "20px",
                    height: "20px",
                    marginLeft: "8px",
                  }}
                />
              )}
            </ToolOption>
          </HorizontalToolBox>
        </Fade>
        <div style={{ position: "relative" }}>
          <ArrowToggle
            id="toggleArrow"
            onClick={() =>
              this.setState({
                isOpen: !this.state.isOpen,
              })
            }
          >
            {this.state.isOpen ? <FaArrowRight /> : <FaArrowLeft />}
          </ArrowToggle>
          {this.state.isOpen && (
            <RightPanel>
              {editor.selectedLayer instanceof NodeLayer ? (
                <NodeLayerPanelView />
              ) : editor.selectedLayer instanceof ConnectionLayer ? (
                <ConnectionLayerPanelView />
              ) : (
                <EmptyRightPanel>
                  Create or Click on a Container Box you've created to enter in
                  flow chart question, answers and narrative responses.
                </EmptyRightPanel>
              )}
            </RightPanel>
          )}
        </div>
        <ErrorModal />
        <QuestionnaireModal />
        <UpgradeDialog
          upgradeDialogOpen={this.upgradeDialogOpen}
          onClose={() => (this.upgradeDialogOpen = false)}
          onConfirm={this.gotoPlan}
        />
      </Container>
    );
  }
}
